import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { useEffect, useState, createContext, useContext } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { requestDefaultUserOrganization } from '@/common/api/requests'
import HgCircularProgressGlobal from '@/common/components/ui/atoms/HgCircularProgressGlobal'
import { Helmet } from '@/common/components/Helmet'
import errorHandler from '@/common/lib/errorHandler'

type DeeplinksContextType = {
  defaultOrg: any
}

const DeeplinksContext = createContext<DeeplinksContextType | null>(null)

export const useDeeplinks = () => {
  const context = useContext(DeeplinksContext)
  if (!context) {
    throw new Error('useDeeplinks must be used within DeeplinksProvider')
  }
  return context
}

export const Route = createFileRoute('/app/deeplinks')({
  component: RouteComponent,
})

function RouteComponent() {
  const [basicDepsReady, setBasicDepsReady] = useState(false)
  const [defaultOrg, setDefaultOrg] = useState<any>(null)
  const [defaultOrgNotFound, setDefaultOrgNotFound] = useState(false)
  const [defaultOrgLoading, setDefaultOrgLoading] = useState(true)

  const appMeta = useSelector((state: any) => state.app_meta)
  const currentUser = useSelector((state: any) => state.auth.currentUser)
  const programs = useSelector((state: any) => state.programs)

  // Set ready once basic deps are loaded
  useEffect(() => {
    let newReady = currentUser.loaded && appMeta.loaded && programs.loaded
    setBasicDepsReady(newReady)
  }, [appMeta.loaded, currentUser.loaded, programs.loaded])

  // Get default org for user
  useEffect(() => {
    if (currentUser.loaded) {
      setDefaultOrgLoading(true)
      requestDefaultUserOrganization()
        .then((res) => {
          let _org = !isEmpty(res.data) ? res.data : null
          if (!_org) {
            setDefaultOrg(null)
            setDefaultOrgNotFound(true)
          } else {
            setDefaultOrg(_org)
            setDefaultOrgNotFound(false)
          }
        })
        .catch((err) => {
          errorHandler(err, false, true)
        })
        .finally(() => {
          setDefaultOrgLoading(false)
        })
    }
  }, [currentUser, currentUser.loaded])

  // Waiting for basic info / redux
  if (!basicDepsReady) {
    return <HgCircularProgressGlobal />
  }

  // Loading
  if (defaultOrgLoading || (!defaultOrg && !defaultOrgNotFound)) {
    return <HgCircularProgressGlobal />
  }

  // Failed to find default org - redirect to dashboard
  if (!defaultOrg && !defaultOrgLoading && defaultOrgNotFound) {
    throw redirect({ to: '/app/account/dashboard' })
  }

  return (
    <DeeplinksContext.Provider value={{ defaultOrg }}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Outlet />
    </DeeplinksContext.Provider>
  )
}
